import { usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { creatorListingsPath, loginPath, menuPath, messagesPath, profilePath } from "@travily/routes";
import { BottomBar, BottomBarItem } from "@ui/components/BottomBar";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";
import { useAccountSwitcher } from "@/layouts/components/account-switcher";
import { isNavActive } from "@/layouts/components/bottom-tabs";
import { EXPLORE_SECTIONS, type ExploreSectionKey } from "@/layouts/components/explore-sections";
import type { SharedProps } from "@/types";

// Looked up via `t(SHORT_LABEL_KEYS[section.key])`, so the i18n-tasks scanner can't see them.
// i18n-tasks-use t('components.frames.app_layout.bottom_bar.programs')
// i18n-tasks-use t('components.frames.app_layout.bottom_bar.destinations')
// i18n-tasks-use t('components.frames.app_layout.bottom_bar.marketplace')

/**
 * Section labels shortened for the bar's ~59px tab cells at a 360px viewport, where the
 * top bar's full `nav.*` wording ellipsizes. Exhaustive so a new section can't fall back
 * to a long label unnoticed.
 */
const SHORT_LABEL_KEYS: Record<ExploreSectionKey, string> = {
  programs: "components.frames.app_layout.bottom_bar.programs",
  destinations: "components.frames.app_layout.bottom_bar.destinations",
  marketplace: "components.frames.app_layout.bottom_bar.marketplace",
};

/**
 * The three browse-section tabs that lead every explorer set, in `EXPLORE_SECTIONS` order.
 * They prefetch on hover rather than the bar's default mount: the bar rides every root route, and
 * each section page runs a search-index query server-side, so mount prefetch would fire three of
 * them on every page load.
 */
function SectionTabs({ currentPath }: { currentPath: string }) {
  return (
    <>
      {EXPLORE_SECTIONS.map((section) => (
        <BottomBarItem
          key={section.key}
          href={section.path}
          icon={section.icon}
          label={t(SHORT_LABEL_KEYS[section.key])}
          color={section.color}
          isActive={isNavActive(currentPath, section.path)}
          prefetch="hover"
        />
      ))}
    </>
  );
}

export interface AppBottomBarProps {
  /** Drives the authenticated tab set. Default `explorer`. Has no effect when unauthenticated. */
  mode?: "explorer" | "creator";
}

/**
 * The mobile bottom navigation bar shown across the app. Reads auth state and
 * current path from Inertia, then picks the matching tab set: Programs /
 * Destinations / Marketplace lead every explorer set, followed by Log in when
 * signed out and Profile + Menu when signed in. Creator mode swaps the whole set
 * for Listings / Messages / Menu. The Explore home carries the bar with no tab
 * active — it is reached from the top bar's logo, not a tab.
 *
 * Most pages get this for free via `PageFrame`'s default `Frame.BottomBar` slot.
 * Pages that bypass PageFrame (split-page surfaces using bare `<Frame>`) render
 * `<AppBottomBar />` themselves inside `<Frame.BottomBar>`.
 */
export function AppBottomBar({ mode = "explorer" }: AppBottomBarProps) {
  const page = usePage<SharedProps>();
  const currentPath = page.url.split("?")[0] ?? page.url;
  const unreadMessagesCount = page.props.unread_messages_count ?? 0;
  const accountSwitcher = useAccountSwitcher();

  if (!accountSwitcher) {
    return (
      <BottomBar aria-label={t("components.bottom_bar.aria_label")} variant="floating">
        <SectionTabs currentPath={currentPath} />
        <BottomBarItem
          href={loginPath()}
          icon="user"
          label={t("components.frames.app_layout.log_in")}
          color="purple"
          isActive={isNavActive(currentPath, loginPath())}
        />
      </BottomBar>
    );
  }

  const currentAccount = accountSwitcher.currentAccount;

  return (
    <BottomBar aria-label={t("components.bottom_bar.aria_label")} variant="floating">
      {mode === "creator" ? (
        <>
          <BottomBarItem
            href={creatorListingsPath()}
            icon="shop"
            label={t("components.frames.app_layout.bottom_bar.listings")}
            color="green"
            isActive={isNavActive(currentPath, creatorListingsPath())}
          />
          <BottomBarItem
            href={messagesPath()}
            icon="message"
            label={t("components.frames.app_layout.bottom_bar.messages")}
            color="yellow"
            badge={unreadMessagesCount > 0}
            isActive={isNavActive(currentPath, messagesPath())}
          />
          <BottomBarItem
            href={menuPath()}
            icon="menu"
            label={t("components.frames.app_layout.bottom_bar.menu")}
            color="neutral"
            isActive={isNavActive(currentPath, menuPath())}
          />
        </>
      ) : (
        <>
          <SectionTabs currentPath={currentPath} />
          {currentAccount?.handle ? (
            <BottomBarItem
              href={profilePath(currentAccount.handle)}
              media={<AccountAvatar account={currentAccount} shape="circle" size="24" />}
              label={t("components.frames.app_layout.bottom_bar.profile")}
              color="blue"
              isActive={isNavActive(currentPath, profilePath(currentAccount.handle))}
            />
          ) : null}
          <BottomBarItem
            href={menuPath()}
            icon="menu"
            label={t("components.frames.app_layout.bottom_bar.menu")}
            color="neutral"
            isActive={isNavActive(currentPath, menuPath())}
          />
        </>
      )}
    </BottomBar>
  );
}
